import { Empty } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import type { AssetListItem } from '~/app/services/asset/asset.service';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { useAssetOverviewTableColumns } from '~/app/hooks/asset/useAssetOverviewTableColumns';
import { useDeleteAsset } from '~/app/hooks/asset/useDeleteAsset';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

type Props = {
  assets: AssetListItem[];
  isLoading: boolean;
  isFetching?: boolean;
  pagination: TablePaginationConfig;
  onTableChange: (pagination: TablePaginationConfig) => void;
};

export function AssetOverviewTable(props: Props) {
  const {
    assets,
    isLoading,
    isFetching = false,
    pagination,
    onTableChange,
  } = props;
  const { t, i18n } = useTranslation('assets');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirmDelete, canDelete } = useDeleteAsset({
    organizationId: user?.organizationId ?? '',
  });
  const columns = useAssetOverviewTableColumns({
    t,
    language: i18n.language,
    onDelete: canDelete ? confirmDelete : undefined,
  });

  const handleChange: TableProps<AssetListItem>['onChange'] = (
    nextPagination,
  ) => {
    onTableChange(nextPagination);
  };

  return (
    <EvimoTable<AssetListItem>
      rowKey="id"
      columns={columns}
      dataSource={assets}
      loading={isLoading || isFetching}
      pagination={pagination}
      onChange={handleChange}
      scroll={{ x: 'max-content' }}
      stickyLastColumn
      locale={{
        emptyText: (
          <Empty
            description={t(
              'overview.empty',
              'No assets found for this organization.',
            )}
          />
        ),
      }}
      onRow={(record) => ({
        onClick: () => navigate(`/assets/${record.id}`),
        style: { cursor: 'pointer' },
      })}
    />
  );
}
